import React, { FC, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Button, CircularProgress, Divider, makeStyles } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import BoldIcon from 'components/icons/BoldIcon';
import ItalicIcon from 'components/icons/ItalicIcon';
import ListIcon from 'components/icons/ListIcon';
import ListOrderedIcon from 'components/icons/ListOrderedIcon';
import ToolbarButton from './ToolbarButton';
import { LinkPopover } from './LinkPopover';
import { isValidUrl } from 'utils/urlUtils';

export const useRichTextStyles = makeStyles(theme => ({
  richText: {
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',

    '& p': {
      margin: '0 0 8px',
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& ul, & ol': {
      paddingLeft: 24,
      margin: '8px 0',
    },
    '& li > p': {
      margin: 0,
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
}));

const useStyles = makeStyles(theme => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
    minHeight: 44,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  editor: {
    '& .ProseMirror': {
      minHeight: 140,
      padding: theme.spacing(1.5),
      outline: 'none',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const hasVisibleText = (editor: Editor): boolean => {
  return editor.state.doc.textContent.replace(/[\s\u200B-\u200D\u2060\uFEFF]/g, '').length > 0;
};

interface TipTapEditorProps {
  html?: string;
  onSubmit: (html: string) => Promise<void>;
}

const TipTapEditor: FC<TipTapEditorProps> = ({ html, onSubmit }) => {
  const classes = useStyles();
  const richTextClasses = useRichTextStyles();
  const initialContentRef = useRef(html);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const mountedRef = useRef(false);

  const [, forceRender] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    mountedRef.current = true;

    const editor = new Editor({
      element: containerRef.current,
      content: initialContentRef.current || '<p></p>',
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          code: false,
          codeBlock: false,
          horizontalRule: false,
          strike: false,
          underline: false,
          trailingNode: false,
          link: {
            openOnClick: false,
            autolink: false,
            linkOnPaste: false,
            isAllowedUri: url => isValidUrl(url),
            HTMLAttributes: {
              target: null,
              rel: null,
              class: null,
            },
          },
        }),
      ],
      onTransaction: () => {
        if (mountedRef.current) {
          forceRender(value => value + 1);
        }
      },
    });

    editorRef.current = editor;
    forceRender(value => value + 1);

    return () => {
      mountedRef.current = false;
      editorRef.current = null;
      editor.destroy();
    };
  }, []);

  useEffect(() => {
    const currentEditor = editorRef.current;
    const content = html || '<p></p>';

    if (currentEditor && currentEditor.getHTML() !== content) {
      currentEditor.commands.setContent(content, { emitUpdate: false });
    }
  }, [html]);

  const editor = editorRef.current;
  const disabled = sending || !editor;

  const handleSubmit = async () => {
    const currentEditor = editorRef.current;
    if (!currentEditor) return;

    setSending(true);

    try {
      await onSubmit(currentEditor.getHTML());

      if (mountedRef.current && editorRef.current === currentEditor) {
        currentEditor.commands.clearContent();
      }
    } catch {}

    setSending(false);
  };

  return (
    <div>
      <div className={classes.root}>
        <div className={classes.toolbar} role="group" aria-label="Comment formatting">
          <ToolbarButton
            disabled={disabled}
            label={<BoldIcon />}
            active={!!editor?.isActive('bold')}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            disabled={disabled}
            label={<ItalicIcon />}
            active={!!editor?.isActive('italic')}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          />
          <Divider orientation="vertical" style={{ height: 24, margin: '0 4px' }} />
          <ToolbarButton
            disabled={disabled}
            label={<ListIcon height={18} width={18} />}
            active={!!editor?.isActive('bulletList')}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            disabled={disabled}
            label={<ListOrderedIcon height={18} width={18} />}
            active={!!editor?.isActive('orderedList')}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          />
          <Divider orientation="vertical" style={{ height: 24, margin: '0 4px' }} />
          <LinkPopover editor={editor} disabled={disabled} />
        </div>

        <div ref={containerRef} className={`${classes.editor} ${richTextClasses.richText}`} />

        <div className={classes.footer}>
          <Button
            type="button"
            color="primary"
            size="small"
            variant="contained"
            endIcon={sending ? <CircularProgress size={18} /> : <SendIcon />}
            disabled={disabled || !hasVisibleText(editor)}
            onClick={handleSubmit}
          >
            Skicka
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TipTapEditor;
